/**
 * Site (project) roles for the portal's invite drawers (#241).
 *
 * The drawers used to offer "Portal role: Member | Admin", which only set the
 * WorkOS org role. The project role was hard-coded to ProjectReader, so nobody
 * could be invited as a Writer, and "Admin" made someone OrgAdmin of every
 * project in the org. These rules pick the SITE role instead and say how a
 * re-add of an existing member is reconciled.
 *
 * Pure: plain data in and out, unit-tested in siteRole.test.ts.
 */

export type SiteRole = 'Reader' | 'Writer' | 'Admin';

/** The org module's ProjectUserRole tag for a site role. */
export type ProjectRoleTag = 'ProjectReader' | 'ProjectWriter' | 'ProjectAdmin';

export const SITE_ROLES: readonly SiteRole[] = ['Reader', 'Writer', 'Admin'];

export function toProjectRoleTag(role: SiteRole): ProjectRoleTag {
  return role === 'Admin' ? 'ProjectAdmin' : role === 'Writer' ? 'ProjectWriter' : 'ProjectReader';
}

export function fromProjectRoleTag(tag: string): SiteRole | null {
  if (tag === 'ProjectAdmin') return 'Admin';
  if (tag === 'ProjectWriter') return 'Writer';
  if (tag === 'ProjectReader') return 'Reader';
  return null;
}

/** One line for the role picker. */
export const SITE_ROLE_HINT: Record<SiteRole, string> = {
  Reader: 'Can take orientations and view the site.',
  Writer: 'Can also build and edit in TurboTwin and the desktop app.',
  Admin: 'Can also manage this site’s people and settings.',
};

/**
 * Why `role` cannot be granted by this viewer, or null when it can. The org
 * module refuses ProjectAdmin from anyone who is not OrgAdmin
 * (`invite_project_user_by_email`: "Only OrgAdmin can assign ProjectAdmin role").
 */
export function siteRoleBlockReason(role: SiteRole, viewerIsOrgAdmin: boolean): string | null {
  if (role === 'Admin' && !viewerIsOrgAdmin) return 'Only an org admin can make someone a site admin.';
  return null;
}

function rank(role: SiteRole): number {
  return role === 'Admin' ? 2 : role === 'Writer' ? 1 : 0;
}

/**
 * Re-adding someone who is already on the site's project: raise their role to
 * the chosen one, never lower it. A bulk paste defaulting to Reader must not
 * demote a site Admin.
 */
export function roleChangeOnReAdd(current: SiteRole, chosen: SiteRole): SiteRole | null {
  return rank(chosen) > rank(current) ? chosen : null;
}

/**
 * The org module's refusals that mean the person is already where we were
 * putting them, as opposed to a real failure:
 *   "User is already a member of this project"
 *   "User already has a pending invitation for this project"
 *   "There is already a pending invitation for this email on this project"
 */
export type AlreadyKind = 'member' | 'pending' | null;

export function classifyAlreadyRefusal(message: string): AlreadyKind {
  if (/already a member/i.test(message)) return 'member';
  if (/already (has a |a )?pending invitation|already (pending|invited)/i.test(message)) return 'pending';
  return null;
}

/** Row of `admin_list_project_users` (its `data.users`). */
export type ProjectUserJson = {
  user_identity: string;
  username: string | null;
  role: string;
};

/**
 * The project member whose username is this email. The org module fills an
 * empty `admin_user.username` from the sign-in email (lowercased), so this
 * matches almost everyone; a member with a custom username is not found, and
 * the caller says so rather than guessing.
 */
export function findMemberByEmail(
  users: readonly ProjectUserJson[],
  email: string,
): ProjectUserJson | null {
  const want = email.trim().toLowerCase();
  if (!want) return null;
  return users.find((u) => (u.username ?? '').trim().toLowerCase() === want) ?? null;
}
